import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ORG_OGU_COMPONENTS_TYPES} from '../../publish/component/store/component.datamodel';
import {Subscription} from 'rxjs';
import {AppService} from '../../application/app.service';
import {ActorObject} from '../../shared/datamodel';
import {
  HIDDEN,
  ORG_OGU_INVALID_CHARACTERS_REGEX,
  OFFICE_INVALID_CHARACTERS_REGEX,
  MAX_HEIGHT,
  MAX_HEIGHT_NUMBER,
  MIN_HEIGHT,
  SCROLL,
  toPixels
} from './inventory-panels.constants';

@Component({
    selector: 'ama-ng-app-store-inventory-panels',
    templateUrl: './inventory-panels.component.html',
    styleUrls: ['./inventory-panels.component.scss'],
    standalone: false
})
export class InventoryPanelsComponent implements OnInit, OnDestroy {
  @Input() selectedActor: ActorObject | undefined | null;
  @Input() selectedActorParentOrg: ActorObject | undefined | null;
  @Input() officesInSelectedEntity = 0;
  @Output() officeSearchChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() orgOguSearchChange: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('oguSearchTextarea') oguSearchTextarea!: ElementRef;
  @ViewChild('officeSearchTextarea') officeSearchTextarea!: ElementRef;

  orgOguSearch = '';
  officeSearch = '';
  canDeployOrgOguLevels = false;

  private readonly subscriptions: Subscription = new Subscription();

  constructor(private readonly appService: AppService) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.appService.detailedApp$.subscribe(app => {
          this.canDeployOrgOguLevels = ORG_OGU_COMPONENTS_TYPES.includes(
            this.appService.getAppComponentsType(app.components, app.applicationType)
          );
        }
      )
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Run the search in the office column
   */
  applyOfficeSearch() {
    this.removeInvalidCharacters();
    this.resizeTextarea();
    this.officeSearchChange.emit(this.officeSearch);
  }

  /**
   * Run the search in the two columns: available elements and deployed elements
   */
  applyORGsAndOGUsSearch() {
    this.removeInvalidCharacters();
    this.resizeTextarea();
    this.orgOguSearchChange.emit(this.orgOguSearch);
  }

  /**
   * Resize both text-areas to fit the content.
   * If the content is empty, the text-area will be resized to 2em.
   * If the content is too big, the text-area will be resized to 300px.
   * Otherwise, the text-area will be resized to the content size.
   */
  private resizeTextarea(): void {
    const oguTextarea: HTMLTextAreaElement = this.oguSearchTextarea.nativeElement;
    const officeTextarea: HTMLTextAreaElement = this.officeSearchTextarea.nativeElement;
    const height: number = Math.max(oguTextarea.scrollHeight, officeTextarea.scrollHeight);
    const clear: boolean = this.orgOguSearch === '' && this.officeSearch === '';
    if (clear) {
      oguTextarea.style.height = MIN_HEIGHT;
      officeTextarea.style.height = MIN_HEIGHT;
      oguTextarea.style.overflowY = HIDDEN;
      officeTextarea.style.overflowY = HIDDEN;
    } else if (height > MAX_HEIGHT_NUMBER) {
      oguTextarea.style.overflowY = SCROLL;
      officeTextarea.style.overflowY = SCROLL;
      oguTextarea.style.height = MAX_HEIGHT;
      officeTextarea.style.height = MAX_HEIGHT;
    } else {
      oguTextarea.style.height = toPixels(height);
      officeTextarea.style.height = toPixels(height);
      oguTextarea.style.overflowY = HIDDEN;
      officeTextarea.style.overflowY = HIDDEN;
    }
  }

  /**
   * Remove invalid characters from the search text
   */
  private removeInvalidCharacters() {
    this.orgOguSearch = this.orgOguSearch.replaceAll(ORG_OGU_INVALID_CHARACTERS_REGEX, '');
    this.officeSearch = this.officeSearch.replaceAll(OFFICE_INVALID_CHARACTERS_REGEX, '');
  }
}
